import type { IbsysKitSettings } from '@ibsys/kit';
import type { TFunction } from 'i18next';
import { formatTimeInterval, TimeIntervalFormat } from '@/shared/lib/utils/string';

type T = TFunction<['common']>;

export const buildKitSettings = (t: T, playerTimeDisplayFormat: TimeIntervalFormat): IbsysKitSettings => ({
  Table: {
    filterApplyText: t('common:accept'),
    filterClearText: t('common:clear'),
    filterBooleanTrueLabel: t('common:yes'),
    filterBooleanFalseLabel: t('common:no'),
    filterTextPlaceholder: t('common:search'),
    filterSelectPlaceholder: t('common:select'),
    sortAscendingText: t('common:sortAscending'),
    sortClearText: t('common:sortClear'),
    sortDescendingText: t('common:sortDescending'),
    filterIntegerFromPlaceholder: t('common:from'),
    filterTimeToPlaceholder: t('common:timeTo'),
    filterIntegerToPlaceholder: t('common:to'),
    filterTimeFromPlaceholder: t('common:timeFrom'),
    emptyMessage: t('common:emptyTable'),
    paginationInfoLabel: ({ start, end, total }) => `${start}-${Math.min(end, total)} of ${total}`,
  },
  DataGrid: {
    defaultPageSize: 50,
    pageSizeOptions: [25, 50, 100],
    resetPageOnFilterChange: true,
    resetPageOnSortChange: true,
    rowHeight: 36,
    highlightHoveredRow: true,
    highlightHoveredColumn: false,
    enableCellSelection: false,
    // Оформление панели управления колонками — одинаково для всех гридов приложения.
    // Состояние конкретного экземпляра (width/onWidthChange/onReset) глобальным быть не может.
    columnsPanel: {
      showPinning: true,
      defaultTab: 'visibility',
    },
    labels: {
      sort: t('common:dataGrid.sort'),
      openFilter: (columnId) => t('common:dataGrid.openFilter', { column: columnId }),
      activeFilter: (columnId) => t('common:dataGrid.activeFilter', { column: columnId }),
      resetFilter: t('common:clear'),
      applyFilter: t('common:accept'),
      // Признак заполненности: подпись тумблера в отборе и сводка свёрнутой карточки в панели
      filterNotEmpty: t('common:dataGrid.filterNotEmpty'),
      filtersPanelValueNotEmpty: t('common:dataGrid.filterNotEmpty'),
      filterSearchPlaceholder: t('common:search'),
      filterNoOptions: t('common:emptyCombobox'),
      // Состояния фильтров, опции которых грузятся с бэкенда (`filter.loadOptions`)
      filterOptionsLoading: t('common:loading'),
      filterOptionsError: t('common:dataGrid.filterOptionsError'),
      filterOptionsRetry: t('common:dataGrid.filterOptionsRetry'),
      selectRow: (rowIndex) => t('common:dataGrid.selectRow', { index: rowIndex }),
      selectAllRows: t('common:dataGrid.selectAllRows'),
      expandRow: t('common:dataGrid.expandRow'),
      collapseRow: t('common:dataGrid.collapseRow'),
      paginationNav: t('common:dataGrid.paginationNav'),
      perPage: t('common:dataGrid.perPage'),
      pageOf: (page, total) => t('common:dataGrid.pageOf', { page, total }),
      firstPage: t('common:dataGrid.firstPage'),
      prevPage: t('common:dataGrid.prevPage'),
      nextPage: t('common:dataGrid.nextPage'),
      lastPage: t('common:dataGrid.lastPage'),
      emptyState: t('common:emptyTable'),
      loadingState: t('common:loading'),
      totalRows: (count) => t('common:dataGrid.totalRows', { count }),
      hideColumn: (name) => t('common:dataGrid.hideColumn', { name }),
      panelTitle: t('common:dataGrid.panelTitle'),
      panelTabVisibility: t('common:dataGrid.panelTabVisibility'),
      panelTabOrder: t('common:dataGrid.panelTabOrder'),
      panelOrderEmpty: t('common:dataGrid.panelOrderEmpty'),
      panelSearchPlaceholder: t('common:search'),
      panelReset: t('common:clear'),
      panelResize: t('common:dataGrid.panelResize'),
      panelNoResults: t('common:dataGrid.panelNoResults'),
      panelToggleVisibility: (name) => t('common:dataGrid.panelToggleVisibility', { name }),
      sidePanelPin: t('common:dataGrid.sidePanelPin'),
      sidePanelUnpin: t('common:dataGrid.sidePanelUnpin'),
      panelPinLeft: t('common:dataGrid.panelPinLeft'),
      panelPinRight: t('common:dataGrid.panelPinRight'),
      panelUnpin: t('common:dataGrid.panelUnpin'),
      panelDragRow: (name) => t('common:dataGrid.panelDragRow', { name }),
      panelDragGroup: (groupName) => t('common:dataGrid.panelDragGroup', { group: groupName }),
      panelToggleAll: t('common:dataGrid.panelToggleAll'),
      panelToggleGroup: (groupName) => t('common:dataGrid.panelToggleGroup', { group: groupName }),
    },
  },
  Datepicker: {
    placeholderFrom: t('common:timeFrom'),
    placeholderTo: t('common:timeTo'),
    placeholder: t('common:date'),
    placeholderTimeFrom: t('common:timeFrom'),
    placeholderTimeTo: t('common:timeTo'),
    placeholderTime: t('common:time.title'),
  },
  Combobox: {
    searchPlaceholder: t('common:search'),
    emptyMessage: t('common:emptyCombobox'),
    loadingMessage: t('common:loading'),
    // Футер списка при множественном выборе
    clearLabel: t('common:clear'),
    selectedCountLabel: (count) => t('common:combobox.selectedCount', { count }),
  },
  AudioPlayer: {
    menuDownloadText: t('common:download'),
    menuSpeedText: t('common:speed'),
    menuSpeedX2Text: t('common:speedX2'),
    menuSpeedX1_5Text: t('common:speedX1_5'),
    menuSpeedX1Text: t('common:speedX1'),
    menuSpeedX0_5Text: t('common:speedX0_5'),
    // Время плеера — интервал от начала записи: показывается в формате компонента «Плеер»
    formatTime: (seconds) => formatTimeInterval(seconds, playerTimeDisplayFormat),
  },
});
